/**
 * What a move does to the board before the server has answered.
 *
 * React-free and in its own file for the reason `listView.ts` states at its top:
 * this is the part of the board that can be wrong in a way a screenshot will not
 * show. A card that lands in the right column while the header above it still
 * counts the old one is not a visual glitch - it is two statements about the
 * same project, one of them false, and the operator has no way to tell which.
 * So the arithmetic is testable without mounting anything.
 *
 * ## Why the counts are adjusted rather than recounted
 *
 * `total` and `done` come from the server and are NOT `tasks.length`. On a
 * project past the endpoint's card cap the columns hold a prefix of the cards
 * while the tallies count every task in the phase, so recomputing them from the
 * visible array would silently shrink every header the moment somebody moved one
 * card. Adjusting by one is correct in both cases, because the card that moved
 * is by definition one of the visible ones.
 *
 * ## Where the card lands inside its new column
 *
 * At the END, and this is a guess rather than a claim. The server orders a
 * column by `sortOrder`, then creation date, then id, and reproducing that here
 * would be a second implementation of an ordering only one side owns. Appending
 * keeps the card visible and adjacent to the drop, and the next load puts it
 * where the server says it goes. The alternative - inserting at a computed
 * position - looks more correct right up to the reload that moves it anyway.
 */
import type { MilestoneBoardColumn } from "../../../lib/milestoneBoard";

/**
 * A column's identity: the phase's id, or null for the unfiled column.
 *
 * Null is a real key here rather than an absence, which is why it is named. The
 * unfiled column is a destination like any other - dropping a card on it clears
 * the task's phase - and code that treated null as "no column chosen" would make
 * unfiling impossible from the one screen where it is obvious.
 */
export type ColumnKey = string | null;

/**
 * Move one card between columns, tallies included.
 *
 * Returns the columns UNCHANGED when the move cannot be described: same column,
 * unknown card, or a destination this board is not drawing. That is deliberate
 * and the caller can rely on it - an unchanged array means nothing was moved, so
 * nothing has to be put back, and a write that goes out anyway is a write whose
 * failure has nothing to snap back from.
 */
export function moveTaskBetweenColumns(
  columns: readonly MilestoneBoardColumn[],
  taskId: string,
  from: ColumnKey,
  to: ColumnKey,
): MilestoneBoardColumn[] {
  if (from === to) return columns as MilestoneBoardColumn[];
  const source = columns.find((c) => c.milestoneId === from);
  const target = columns.find((c) => c.milestoneId === to);
  if (!source || !target) return columns as MilestoneBoardColumn[];
  const task = source.tasks.find((x) => x.id === taskId);
  if (!task) return columns as MilestoneBoardColumn[];

  // Counted once and applied to both ends, so a done card cannot leave one
  // column as finished work and arrive in the other as unfinished.
  const wasDone = task.status === "done" ? 1 : 0;

  return columns.map((column) => {
    if (column.milestoneId === from) {
      return {
        ...column,
        tasks: column.tasks.filter((x) => x.id !== taskId),
        total: column.total - 1,
        done: column.done - wasDone,
      };
    }
    if (column.milestoneId === to) {
      // The task carries its new phase with it. Without this the card would be
      // in the right column while its own record still named the old one, and
      // the move menu on it would tick a phase it is no longer under.
      return {
        ...column,
        tasks: [...column.tasks, { ...task, milestoneId: to }],
        total: column.total + 1,
        done: column.done + wasDone,
      };
    }
    return column;
  });
}
